import { WorkflowIcon } from '@alison/ui';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { type ReactNode, useId, useState } from 'react';
import { humanize } from './catalogFilters.js';
import { IconBadge } from './IconBadge.js';
import { SelectableCard } from './SelectableCard.js';
import { iconFor } from './workflowIcons.js';
import type { Stage, WorkflowCard as WorkflowCardData } from './workflows.types.js';

interface WorkflowCardProps {
  card: WorkflowCardData;
  stage: Stage;
  stageLabel: string;
  /** `goal` is set when the card is reached through one of its entry points. */
  onOpen: (workflowId: string, goal?: string) => void;
  footer?: string;
}

function startsFrom(entryPoints: WorkflowCardData['entryPoints']): string | null {
  const [first] = entryPoints;
  if (first === undefined) return null;
  return entryPoints.length === 1
    ? `Starts from ${first.name}`
    : `Starts from ${entryPoints.length} workflows`;
}

export function WorkflowCard({
  card,
  stage,
  stageLabel,
  onOpen,
  footer,
}: WorkflowCardProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const runs = `${card.runCount} ${card.runCount === 1 ? 'Run' : 'Runs'}`;
  // A cold start that is also chained into opens itself, like any cold start.
  const entryPoints = card.chainOnly ? card.entryPoints : [];
  const [onlyEntry] = entryPoints;
  const menuId = useId();
  const headerId = useId();
  const hasMenu = entryPoints.length > 1;

  return (
    <>
      <SelectableCard
        testId={`workflow-card-${card.workflowId}`}
        onClick={(event) => {
          if (hasMenu) setAnchor(event.currentTarget);
          else if (onlyEntry !== undefined) onOpen(onlyEntry.workflowId, card.workflowId);
          else onOpen(card.workflowId);
        }}
        {...(card.suggested ? { accent: 'border.light' } : {})}
        {...(hasMenu ? { menu: { id: menuId, expanded: anchor !== null } } : {})}
        icon={
          <IconBadge
            icon={iconFor(card.icon, WorkflowIcon)}
            size={32}
            iconSize={18}
            sx={{
              bgcolor: (theme) => theme.palette.flightStage[stage].bg,
              color: (theme) => theme.palette.flightStage[stage].fg,
            }}
          />
        }
        title={card.name}
        tooltip={card.tooltip}
        subtitle={humanize(card.role)}
        note={card.suggested ? 'Suggested' : null}
        description={card.description}
        detail={startsFrom(entryPoints)}
        chips={card.outcomes.map((outcome) => humanize(outcome).toUpperCase())}
        footer={footer ?? `${stageLabel} · ${humanize(card.category)} · ${runs}`}
      />
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: { sx: { borderRadius: 1 } },
          list: { 'aria-labelledby': headerId },
        }}
      >
        <ListSubheader
          id={headerId}
          sx={{ px: 3, fontSize: 11, fontWeight: 600, lineHeight: 2.5, color: 'text.secondary' }}
        >
          Start from
        </ListSubheader>
        {entryPoints.map((entry) => (
          <MenuItem
            key={entry.workflowId}
            onClick={() => {
              setAnchor(null);
              onOpen(entry.workflowId, card.workflowId);
            }}
            sx={{ fontSize: 13 }}
          >
            {entry.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
